/**
 * Bridge between the existing fakeBrain response and the new
 * recommendationEngine. Pure heuristics keep it self-contained: we derive
 * `facts` from the intent + slots without hitting any datasource.
 *
 * The chat UI consumes the result via `Message.recommendation` and renders
 * `AgentRecommendationChip`.
 */

import type { IntentResponse } from '@/agent/fakeBrain';
import {
  evaluateRecommendations,
  type RecommendationTrigger,
} from '@/agent/recommendationEngine';
import {
  createConversationContext,
  type ConversationContext,
} from '@/agent/conversationContext';
import type { MessageRecommendation, UIAction } from '@/agent/agentStore';

const INTENT_REMAP: Record<string, string> = {
  open_camera_detail: 'devices_open_camera_detail',
  open_camera_health: 'devices_open_camera_health',
  open_camera_kpis: 'devices_open_camera_kpis',
  open_camera_calibration: 'devices_open_camera_calibration',
  open_camera_history: 'devices_open_camera_history',
  open_camera_config: 'devices_open_camera_config',
  open_camera_live: 'devices_open_camera_live',
  open_camera_compare: 'devices_open_camera_compare',
  open_edge_detail: 'devices_open_edge_detail',
  open_edge_metrics: 'devices_open_edge_metrics',
  open_plc_detail: 'devices_open_plc_detail',
  open_device_firmware: 'devices_open_firmware',
  open_network_topology: 'open_network_topology',
  open_floor_map: 'open_floor_map',
  devices_open_sla: 'devices_open_sla',
  open_wo_detail: 'open_wo_detail',
  open_wo_timeline: 'open_wo_timeline',
  open_wo_bom: 'open_wo_bom',
};

function paramsOf(uiActions?: UIAction[]): Record<string, unknown> {
  const tool = uiActions?.find((a) => a.type === 'execute_tool');
  return tool && 'params' in tool ? { ...tool.params } : {};
}

/**
 * Heuristic facts: in a real backend these would come from the data layer.
 * For demo purposes we mark every camera detail as "warning" with 50%
 * probability based on its numeric id, every WO with id ending in 42 as
 * having backorders, etc. Deterministic and unit-test friendly.
 */
function deriveFacts(intentId: string, slots: Record<string, unknown>): RecommendationTrigger['facts'] {
  const facts: RecommendationTrigger['facts'] = {};
  const camId = slots.cameraId as string | undefined;
  if (camId) {
    facts.cameraId = camId;
    const n = Number(camId.replace(/\D/g, '')) || 0;
    facts.healthStatus = n % 4 === 0 ? 'critical' : n % 2 === 0 ? 'warning' : 'ok';
    facts.edgeOwner = n <= 3 ? 'EDGE-01' : 'EDGE-02';
  }
  if (slots.edgeId) {
    facts.edgeId = slots.edgeId as string;
    facts.edgeKpiMaxPct = 92; // demo: triggers REC-03
  }
  const woId = slots.woId as string | undefined;
  if (woId) {
    facts.woId = woId;
    const tail = Number(woId.slice(-2)) || 0;
    facts.woHasBackorders = tail % 2 === 0;
    facts.woBackorderCount = facts.woHasBackorders ? 3 : 0;
    facts.woStatus = 'in_progress';
  }
  return facts;
}

export function buildRecommendation(
  response: IntentResponse,
  ctx: ConversationContext = createConversationContext(),
  now = Date.now(),
): MessageRecommendation | null {
  const intentId = response.intentId ?? '';
  const remapped = INTENT_REMAP[intentId];
  if (!remapped) return null;

  const slots = paramsOf(response.uiActions);
  const facts = deriveFacts(remapped, slots);

  const rec = evaluateRecommendations(
    { intent: remapped, slots, facts },
    ctx,
    now,
  );
  if (!rec) return null;

  return {
    id: rec.id,
    message: rec.message,
    priority: rec.priority,
    // Build a follow-up text that fakeBrain can re-classify on Yes
    acceptText: synthesizeAcceptText(rec.intent, rec.slots),
    shownAt: now,
  };
}

function synthesizeAcceptText(
  intent: string,
  slots: Record<string, unknown>,
): string {
  switch (intent) {
    case 'devices_open_camera_health':
      return `salud ${slots.cameraId}`;
    case 'devices_open_camera_kpis':
      return `KPIs ${slots.cameraId}`;
    case 'devices_open_camera_calibration':
      return `calibración ${slots.cameraId}`;
    case 'devices_open_edge_metrics':
      return slots.range
        ? `métricas ${slots.edgeId} últimas ${slots.range}`
        : `métricas ${slots.edgeId}`;
    case 'devices_open_edge_detail':
      return `abre ${slots.edgeId}`;
    case 'open_wo_bom':
      return slots.filterStatus === 'backorder'
        ? `BOM ${slots.woId} solo backorders`
        : `BOM ${slots.woId}`;
    case 'open_wo_timeline':
      return `timeline ${slots.woId}`;
    case 'open_wo_detail':
      return `abre ${slots.woId}`;
    case 'open_floor_map':
      return `mapa de planta`;
    case 'open_network_topology':
      return `topología`;
    case 'devices_open_sla':
      return `SLA`;
    default:
      return `sí`;
  }
}
